import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { SEO } from '../../../components/SEO';
import {
  ArrowLeft, LayoutGrid, ExternalLink, ChevronDown,
  LayoutPanelTop, BadgeCheck, Table2, HelpCircle,
  Images, Tags, Languages, BarChart2,
} from 'lucide-react';

/* ─── Brand ───────────────────────────────────────────────────────────── */
const PRODUCTLY_VIOLET = '#7C3AED';

/* ─── Feature data ────────────────────────────────────────────────────── */
const features = [
  {
    id: 1, icon: LayoutPanelTop, color: '#7C3AED', bg: '#F3EEFF',
    title: 'Custom Content Blocks',
    desc: 'Design rich enrichment blocks — text, media, callouts — in the admin and drop them onto any product page. No theme code, no developer needed.',
  },
  {
    id: 2, icon: Table2, color: '#1A73E8', bg: '#EBF4FF',
    title: 'Specs & Comparison Tables',
    desc: 'Build structured spec tables and side-by-side comparisons so shoppers get every detail they need to buy with confidence.',
  },
  {
    id: 3, icon: BadgeCheck, color: '#059669', bg: '#ECFDF5',
    title: 'Trust Badges',
    desc: 'Add shipping, warranty, returns, and quality badges that reassure shoppers right where the buying decision happens.',
  },
  {
    id: 4, icon: HelpCircle, color: '#D97706', bg: '#FFFBEB',
    title: 'Product FAQs',
    desc: 'Answer common questions inline on the product page. Cut down support tickets and remove last-minute hesitation.',
  },
  {
    id: 5, icon: LayoutGrid, color: '#0891B2', bg: '#EFF6FF',
    title: 'Tabbed Content',
    desc: 'Organise descriptions, specs, care instructions, and FAQs into clean tabs — a tidy product page that never overwhelms.',
  },
  {
    id: 6, icon: Images, color: '#DC2626', bg: '#FEF2F2',
    title: 'Extra Media',
    desc: 'Embed additional images, videos, and visual content beyond the default gallery to tell the full product story.',
  },
  {
    id: 7, icon: Tags, color: '#7C3AED', bg: '#F3EEFF',
    title: 'Assign by Product or Collection',
    desc: 'Apply enrichment blocks to a single product, a whole collection, or your entire catalog in one move. Targeted, not tedious.',
  },
  {
    id: 8, icon: BarChart2, color: '#059669', bg: '#ECFDF5',
    title: 'Usage-Based Billing',
    desc: 'Only pay for what you use. Enrichment rendering is metered at a transparent rate with a billing dashboard built into the app.',
  },
];

/* ─── Shared dark section ─────────────────────────────────────────────── */
const DarkBg = ({ children, className = '' }) => (
  <section className={`relative overflow-hidden ${className}`} style={{ background: '#0B1426' }}>
    <div style={{
      position: 'absolute', inset: 0, pointerEvents: 'none',
      backgroundImage: 'linear-gradient(rgba(124,58,237,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(124,58,237,0.07) 1px, transparent 1px)',
      backgroundSize: '48px 48px',
    }} />
    <div style={{ position: 'absolute', top: '-18%', right: '-9%', width: 580, height: 580, background: 'radial-gradient(circle, rgba(124,58,237,0.16) 0%, transparent 70%)', pointerEvents: 'none' }} />
    <div style={{ position: 'absolute', bottom: '-15%', left: '-5%', width: 420, height: 420, background: 'radial-gradient(circle, rgba(26,115,232,0.09) 0%, transparent 70%)', pointerEvents: 'none' }} />
    <div className="relative z-10">{children}</div>
  </section>
);

/* ─── Top bar ─────────────────────────────────────────────────────────── */
const ProductlyTopBar = () => (
  <div
    className="sticky top-0 z-50 border-b border-[#E2E8F0]"
    style={{ background: 'rgba(248,247,244,0.95)', backdropFilter: 'blur(20px)' }}
  >
    <div className="container-custom py-4 flex items-center justify-between">
      <Link
        to="/"
        className="flex items-center gap-2 text-[#374151] hover:text-[#0F172A] text-sm font-medium transition-colors group"
      >
        <ArrowLeft size={15} className="group-hover:-translate-x-0.5 transition-transform duration-200" />
        Back to site
      </Link>

      <div className="flex items-center gap-2">
        <div className="w-7 h-7 flex items-center justify-center rounded-sm" style={{ backgroundColor: PRODUCTLY_VIOLET }}>
          <LayoutGrid size={15} color="#fff" />
        </div>
        <span className="font-display text-[1.2rem] font-bold text-[#0F172A]">Productly</span>
      </div>

      <a
        href="#"
        className="hidden sm:inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold text-white transition-opacity hover:opacity-90"
        style={{ backgroundColor: '#95BF47' }}
      >
        Add to Shopify
        <ExternalLink size={12} />
      </a>
    </div>
  </div>
);

/* ─── Footer strip ────────────────────────────────────────────────────── */
const ProductlyFooter = () => (
  <div className="border-t border-[#E2E8F0] py-6">
    <div className="container-custom flex flex-col sm:flex-row items-center justify-between gap-3">
      <p className="font-mono text-[0.55rem] tracking-widest text-[#9CA3AF] uppercase">
        © {new Date().getFullYear()} Productly · A Smart Solutions Product
      </p>
      <div className="flex items-center gap-4">
        <Link to="/apps/productly/terms" className="font-mono text-[0.55rem] tracking-widest text-[#9CA3AF] hover:text-[#374151] uppercase transition-colors">Terms</Link>
        <span className="text-[#E2E8F0]">·</span>
        <Link to="/apps/productly/privacy" className="font-mono text-[0.55rem] tracking-widest text-[#9CA3AF] hover:text-[#374151] uppercase transition-colors">Privacy</Link>
      </div>
    </div>
  </div>
);

/* ─── Page ────────────────────────────────────────────────────────────── */
export const ProductlyFeaturesPage = () => {
  return (
    <div className="min-h-screen bg-[#F8F7F4] flex flex-col">
      <SEO
        title="Productly — Rich Product Page Enrichment for Shopify"
        description="Productly is a Shopify app that enriches product pages with custom content blocks, spec tables, trust badges, FAQs, tabbed content, and extra media — all from the admin, no theme code required."
        path="/apps/productly"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: 'Productly',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Shopify',
          description: 'Productly is a Shopify app that enriches product pages with custom content blocks, spec tables, trust badges, FAQs, tabbed content, and extra media — configured from the Shopify admin without editing theme code.',
          offers: {
            '@type': 'Offer',
            priceCurrency: 'USD',
            price: '0',
          },
          publisher: {
            '@type': 'Organization',
            name: 'Smart Solutions',
            url: 'https://smartsolution.ai',
          },
        }}
      />
      <ProductlyTopBar />

      {/* ── HERO ── */}
      <DarkBg>
        <div className="container-custom py-28 md:py-40">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-3xl"
          >
            {/* Badge */}
            <div className="flex items-center gap-3 mb-8">
              <span
                className="inline-flex items-center gap-2 px-3 py-1.5 border text-[0.65rem] font-mono tracking-widest uppercase"
                style={{ borderColor: 'rgba(124,58,237,0.35)', color: '#A78BFA', backgroundColor: 'rgba(124,58,237,0.08)' }}
              >
                <span className="w-1.5 h-1.5 rounded-full pulse-dot" style={{ backgroundColor: '#7C3AED' }} />
                Shopify Embedded App
              </span>
              <span className="w-6 h-px" style={{ backgroundColor: 'rgba(124,58,237,0.3)' }} />
            </div>

            {/* Headline */}
            <h1
              className="font-display font-bold text-white mb-6 leading-[1.05]"
              style={{ fontSize: 'clamp(2.5rem, 6vw, 4.5rem)' }}
            >
              Richer Product Pages,{' '}
              <span className="italic" style={{ color: '#A78BFA' }}>Zero</span>{' '}
              Code.
            </h1>

            {/* Subtitle */}
            <p
              className="text-[#94A3B8] leading-relaxed mb-10 max-w-xl"
              style={{ fontSize: 'clamp(1rem, 2vw, 1.125rem)' }}
            >
              Productly lets merchants enrich product pages with custom content blocks —
              spec tables, trust badges, tabbed content, FAQs, and extra media — designed
              right inside the Shopify admin and rendered on the storefront. No theme edits required.
            </p>

            {/* CTAs */}
            <div className="flex flex-wrap gap-4 mb-16">
              <a
                href="#"
                className="inline-flex items-center gap-2.5 px-6 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90"
                style={{ backgroundColor: '#95BF47' }}
              >
                <img src="/resources/shopify.png" className="w-4 h-4 object-contain" alt="Shopify" />
                Add to Shopify
              </a>
              <a
                href="#features"
                className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium border transition-colors hover:border-white/40 hover:text-white"
                style={{ borderColor: 'rgba(148,163,184,0.4)', color: '#94A3B8' }}
              >
                Explore Features
                <ChevronDown size={14} />
              </a>
            </div>

            {/* Stats */}
            <div
              className="grid grid-cols-3 gap-8 pt-8"
              style={{ borderTop: '1px solid rgba(148,163,184,0.15)' }}
            >
              {[
                { num: '8', label: 'Core Features' },
                { num: 'No-Code', label: 'Setup' },
                { num: 'Pay-as-you-go', label: 'Billing Model' },
              ].map(({ num, label }) => (
                <div key={label}>
                  <div
                    className="font-display font-bold text-white mb-1"
                    style={{ fontSize: 'clamp(1.1rem, 2.5vw, 1.6rem)' }}
                  >
                    {num}
                  </div>
                  <div className="font-mono text-[0.6rem] tracking-widest text-[#64748B] uppercase">{label}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Bottom fade */}
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0, height: 80,
          background: 'linear-gradient(to bottom, transparent, #F8F7F4)',
          pointerEvents: 'none',
        }} />
      </DarkBg>

      {/* ── FEATURES ── */}
      <section id="features" className="section-padding bg-[#F8F7F4] flex-1">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-12"
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="section-label">What Productly Does</span>
              <span className="w-6 h-px bg-[#2563EB]/30" />
            </div>
            <h2
              className="font-display font-bold text-[#0F172A]"
              style={{ fontSize: 'clamp(2rem, 4vw, 3.5rem)' }}
            >
              Built to{' '}
              <span className="italic text-gold">convert</span>{' '}
              shoppers.
            </h2>
            <p className="text-[#374151] max-w-md mt-4 text-sm leading-relaxed">
              Eight features working together to give merchants richer, higher-converting
              product pages — without ever touching theme code.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {features.map((feat, i) => (
              <motion.div
                key={feat.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="bg-white border border-[#E2E8F0] p-8 group hover:border-[#C7D2FE] transition-all duration-300"
                style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}
              >
                <div className="flex items-start gap-5">
                  <div
                    className="w-12 h-12 flex items-center justify-center shrink-0 border border-[#E2E8F0]"
                    style={{ backgroundColor: feat.bg }}
                  >
                    <feat.icon size={22} style={{ color: feat.color }} />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-bold text-[#0F172A] mb-2 group-hover:text-[#2563EB] transition-colors duration-300">
                      {feat.title}
                    </h3>
                    <p className="text-[#374151] text-sm leading-relaxed">{feat.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <DarkBg className="py-24">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl mx-auto"
          >
            <div className="flex items-center justify-center gap-3 mb-6">
              <span className="w-6 h-px" style={{ backgroundColor: 'rgba(124,58,237,0.4)' }} />
              <span
                className="font-mono text-[0.65rem] tracking-[0.25em] uppercase"
                style={{ color: '#A78BFA' }}
              >
                Get Started
              </span>
              <span className="w-6 h-px" style={{ backgroundColor: 'rgba(124,58,237,0.4)' }} />
            </div>

            <h2
              className="font-display font-bold text-white mb-6 leading-[1.1]"
              style={{ fontSize: 'clamp(2rem, 4.5vw, 3.5rem)' }}
            >
              Ready to enrich your{' '}
              <span className="italic" style={{ color: '#A78BFA' }}>first product</span>?
            </h2>

            <p className="text-[#94A3B8] mb-10 leading-relaxed max-w-lg mx-auto">
              Join merchants using Productly to build richer, higher-converting product pages on Shopify.
            </p>

            <a
              href="#"
              className="inline-flex items-center gap-2.5 px-8 py-4 text-sm font-semibold text-white transition-opacity hover:opacity-90"
              style={{ backgroundColor: '#95BF47' }}
            >
              <img src="/resources/shopify.png" className="w-4 h-4 object-contain" alt="Shopify" />
              Add to Shopify — It's Free to Try
            </a>
          </motion.div>
        </div>
      </DarkBg>

      <ProductlyFooter />
    </div>
  );
};
